import {useQueryClient} from '@tanstack/react-query'
import {useAddNotification} from '@yandex-delivery/blocks'
import {useI18n} from '@yandex-delivery/core/i18n'
import type {V2FormResponse} from '@yandex-delivery/types/schemas/services/delivery-corp-account-api/docs/yaml/definitions/delivery_form'
import {getErrorMessage} from '@yandex-delivery/utils'
import {useCallback} from 'react'
import {useFormContext} from 'react-hook-form'

import {appConfig} from 'src/shared/config'

import type {FormWidgetConfig} from '../../../../../../../../packages/form'
import {fetchDeliveryForm} from '../../../../../shared/api/deprecated/fetch-verticals'
import {store} from '../store'
import {matchPattern} from '../utils/matcher'
import {toFormState} from '../utils/toFormState'
import {useDefaultValuesAndDraftSync} from './useDefaultValuesAndDraftSync'

/**
 * Возвращает метод для запроса конфига формы.
 */
export const useGetDeliveryFormData = () => {
  const i18n = useI18n()
  const addNotification = useAddNotification()
  const {clearDraftValues} = useDefaultValuesAndDraftSync()
  const queryClient = useQueryClient()
  const verticalName = useFormContext().watch('offerFlow.vertical.name')

  return useCallback(async (): Promise<FormWidgetConfig | {}> => {
    const descriptors = store.state.descriptors
    const state = store.state.formRequest.state

    /** Если в descriptor есть подходящий match и ttl оффера не истек, вместо запроса используется layout из него */
    const descriptorMatched = descriptors?.find(descriptor => matchPattern(descriptor.match, state))

    if (descriptorMatched && store.state.ttl !== 0) {
      store.setState(prev => ({
        ...prev,
        offerId: descriptorMatched.override.offer?.offer_id,
      }))

      return descriptorMatched.override.layout as FormWidgetConfig
    }

    let data: V2FormResponse | undefined
    const mode =
      store.state.settings && Object.keys(store.state.settings).includes(verticalName)
        ? store.state.settings[verticalName]?.mode
        : undefined

    try {
      if (mode) {
        const body = {
          mode,
          user_info: store.state.formRequest.user_info,
          state: toFormState(store.state.formRequest.state),
        }
        data = await fetchDeliveryForm(body, appConfig.countryCode)
      }
    } catch (error: unknown) {
      addNotification(
        error && error instanceof Error ? getErrorMessage(error) : i18n('request.mutation.error', 'Ошибка запроса'),
        {
          type: 'error',
        },
      )

      // При ошибке запроса очищаем драфт
      clearDraftValues()
    }

    const ttl = data?.descriptors?.[0]?.override?.offer?.ttl
    if (data) {
      store.setState(prev => ({
        ...prev,
        descriptors: data?.descriptors,
        offerId: data?.descriptors?.[0]?.override?.offer?.offer_id,
        ttl,
      }))
    }

    if (ttl) {
      setTimeout(() => {
        /** сбрасываю ttl оффера, чтобы перезапрос не прервался на матчере */
        store.state.ttl = 0
        void queryClient.invalidateQueries({queryKey: ['orderForm']})
      }, ttl * 1000)
    }

    return data?.descriptors[0].override.layout as FormWidgetConfig | {}
  }, [addNotification, clearDraftValues, i18n, queryClient, verticalName])
}
